import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';

import CheckoutModal from '../components/CheckoutModal';
import DetectionViewer from '../components/result/DetectionViewer';
import PersonalInfoSection from '../components/result/PersonalInfoSection';
import ProductCards from '../components/result/ProductCards';
import RadarAnalysisSection from '../components/result/RadarAnalysisSection';
import ResultAnalysisSections from '../components/result/ResultAnalysisSections';
import SegmentationRenderer, { useSegmentation } from '../components/result/SegmentationRenderer';
import useSkinResultStore from '../store/skinResultStore';
import { usePageSetupLogo } from '../hooks/usePageSetupLogo';
import { useReportImages } from '../hooks/useReportImages';
import { useSkinProductCart } from '../hooks/useSkinProductCart';
import {
  fetchSkinReport,
  isAppSessionJwt,
  isSignedReportAccessToken,
} from '../utils/skinReportApi';
import {
  REQUEST_STATUS,
  createRequestScope,
  isAbortError,
  isTimeoutError,
  logSkinResilience,
} from '../utils/skinResilience';

import '../styles/PageBackground.css';
import './SkinRoutinePage.css';
import './SkinResultPage.css';

function getReportAppSessionToken(report) {
  return String(report?.app_session_token || '').trim();
}

const SkinResultPageContent = ({ report, skinReference, appSessionToken }) => {
  const segmentation = useSegmentation();
  const orgLogoUrl = usePageSetupLogo(appSessionToken);
  const logoSrc = orgLogoUrl || `${process.env.PUBLIC_URL}/logo.svg`;
  const { images } = useReportImages(report, skinReference, appSessionToken);

  const customerDetails = useMemo(() => {
    const user = report?.user || {};
    return {
      name: user.name || user.Name || '',
      phone: user.mobile_number || user.mobileNumber || user.phone || '',
      email: user.email || user.Email || '',
    };
  }, [report?.user]);

  const {
    cartItems,
    checkoutCartItems,
    isCheckoutModalOpen,
    handleAddToCart,
    handleCheckout,
    handleCloseCheckoutModal,
    handleCartUpdate,
    handleViewProduct,
    getTotalCartItems,
    apiBaseUrl,
  } = useSkinProductCart({
    sessionId: skinReference,
    appSessionToken,
    pageName: 'SkinResultPage',
    customerDetails,
  });

  return (
    <>
      <div className="skin-routine-page-nav skin-result-page-nav" id="skin-result-page-nav">
        <div className="skin-routine-page-title-wrapper">
          {segmentation.addToCart && getTotalCartItems() > 0 && (
            <button
              type="button"
              className="skin-routine-page-checkout-button-icon"
              onClick={handleCheckout}
              title={`Checkout (${getTotalCartItems()} items)`}
              aria-label={`Checkout with ${getTotalCartItems()} items`}
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="9" cy="21" r="1" />
                <circle cx="20" cy="21" r="1" />
                <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6" />
              </svg>
              <span className="skin-routine-page-checkout-badge-icon">{getTotalCartItems()}</span>
            </button>
          )}
          <img
            src={logoSrc}
            alt="Organization logo"
            className="skin-result-org-logo-nav"
          />
        </div>
      </div>

      <main className="skin-routine-page-main" id="skin-result-page-main">
        <div className="skin-routine-page-container" id="skin-result-page-container">
          <section className="skin-routine-page-layout" id="skin-result-page-layout">
            <div
              className="skin-routine-user-info-intro skin-text-body skin-text-medium"
              style={{ textAlign: 'center', margin: '0 0 1.2rem 0' }}
            >
              <span>
                This is your personalized skin analysis summary. Review your profile, score,
                and recommendations below for a complete overview of your skin health.
              </span>
            </div>

            <PersonalInfoSection report={report} userImage={images.front} />

            <div className="skin-routine-content-wrapper skin-routine-content-no-tabs skin-routine-sections">
              {!segmentation.loaded ? (
                <div className="skin-routine-summary-loading">
                  <div className="loading-spinner" />
                  <p className="skin-routine-summary-loading-text">Loading session settings…</p>
                </div>
              ) : (
                <>
                  <DetectionViewer
                    report={report}
                    skinReference={skinReference}
                    appSessionToken={appSessionToken}
                  />
                  <ResultAnalysisSections report={report} />
                  <RadarAnalysisSection
                    report={report}
                    skinReference={skinReference}
                    appSessionToken={appSessionToken}
                  />
                  <ProductCards
                    products={report?.products}
                    cartItems={cartItems}
                    onAddToCart={handleAddToCart}
                    onViewProduct={handleViewProduct}
                  />

                  {segmentation.addToCart ? (
                    <div className="skin-routine-page-actions" id="skin-result-page-actions">
                      <div className="skin-routine-page-actions-buttons">
                        <button
                          type="button"
                          className="skin-action-button skin-routine-checkout-button"
                          onClick={handleCheckout}
                          title="Checkout"
                        >
                          Checkout
                        </button>
                      </div>
                    </div>
                  ) : null}
                </>
              )}
            </div>
          </section>
        </div>
      </main>

      <CheckoutModal
        isOpen={isCheckoutModalOpen}
        onClose={handleCloseCheckoutModal}
        cartItems={checkoutCartItems}
        onCartUpdate={handleCartUpdate}
        sessionId={skinReference}
        apiBaseUrl={apiBaseUrl}
        token={appSessionToken}
        customerDetails={customerDetails}
      />
    </>
  );
};

const SkinResultLoadedView = ({ report, skinReference, appSessionToken }) => (
  <SegmentationRenderer
    key={appSessionToken || skinReference}
    appSessionToken={appSessionToken}
  >
    <SkinResultPageContent
      report={report}
      skinReference={skinReference}
      appSessionToken={appSessionToken}
    />
  </SegmentationRenderer>
);

const STATUS_COPY = {
  [REQUEST_STATUS.PROCESSING]: 'Loading your skin report…',
  [REQUEST_STATUS.RETRYING]: 'Retrying report load…',
  [REQUEST_STATUS.RECONNECTING]: 'Reconnecting to server…',
  [REQUEST_STATUS.TIMEOUT]: 'Report load timed out.',
};

const SkinResultPage = () => {
  const [params] = useSearchParams();
  const skinReference = params.get('skinreference') || params.get('skin_reference') || '';
  const queryToken = params.get('token') || '';
  const signedReportToken = useMemo(
    () => (isSignedReportAccessToken(queryToken) ? queryToken.trim() : ''),
    [queryToken],
  );

  useEffect(() => {
    const raw = params.get('token') || params.get('app_session_token') || '';
    if (!raw || isSignedReportAccessToken(raw)) return;
    if (!isAppSessionJwt(raw) && !params.get('app_session_token')) return;

    const next = new URLSearchParams(params);
    next.delete('token');
    next.delete('app_session_token');
    const qs = next.toString();
    const path = window.location.pathname;
    window.history.replaceState(null, '', qs ? `${path}?${qs}` : path);
    logSkinResilience('report_url_sanitized', {
      skinReference,
      removed: isAppSessionJwt(raw) ? 'app_session_jwt' : 'app_session_token',
    });
  }, [params, skinReference]);

  const {
    report,
    loading,
    error,
    requestStatus,
    setReport,
    setLoading,
    setError,
    setRequestStatus,
  } = useSkinResultStore();

  const appSessionToken = useMemo(
    () => getReportAppSessionToken(report),
    [report],
  );

  const requestScopeRef = useRef(null);
  const retryBusyRef = useRef(false);

  const abortActiveRequest = useCallback((reason) => {
    if (requestScopeRef.current) {
      logSkinResilience('abort', { label: 'skin-report-page', reason });
      requestScopeRef.current.abort(reason);
      requestScopeRef.current = null;
    }
  }, []);

  const loadReport = useCallback(async (ref, token, { isManualRetry = false, silent = false } = {}) => {
    if (!ref || (isManualRetry && retryBusyRef.current)) return;

    if (isManualRetry) retryBusyRef.current = true;

    abortActiveRequest('retry-replacement');
    const scope = createRequestScope();
    requestScopeRef.current = scope;

    if (!silent) {
      setLoading(true);
      setError(null);
      setRequestStatus(REQUEST_STATUS.PROCESSING);
    }

    try {
      const data = await fetchSkinReport(ref, {
        accessToken: token || undefined,
        signal: scope.signal,
        onRetry: ({ reconnecting }) => {
          if (!silent) {
            setRequestStatus(reconnecting ? REQUEST_STATUS.RECONNECTING : REQUEST_STATUS.RETRYING);
          }
        },
      });

      if (scope.isAborted()) return;

      setReport(data);
      if (!silent) {
        setRequestStatus(REQUEST_STATUS.IDLE);
      }
    } catch (err) {
      if (isAbortError(err)) return;

      if (!silent) {
        if (isTimeoutError(err)) {
          setError('Report load timed out. Please try again.');
          setRequestStatus(REQUEST_STATUS.TIMEOUT);
        } else if (err.status === 403 || err.apiResponse?.code === 'expired_token') {
          setError(err.message || 'This share link has expired.');
          setRequestStatus(REQUEST_STATUS.FAILED);
        } else if (err.status === 401 || err.apiResponse?.code === 'invalid_token') {
          setError(err.message || 'Invalid or missing access token.');
          setRequestStatus(REQUEST_STATUS.FAILED);
        } else {
          setError(err.message || 'Failed to load skin report');
          setRequestStatus(REQUEST_STATUS.RETRY_AVAILABLE);
        }

        setReport(null);
      }
    } finally {
      retryBusyRef.current = false;
      if (!scope.isAborted() && !silent) {
        setLoading(false);
      }
      if (requestScopeRef.current === scope) {
        requestScopeRef.current = null;
      }
    }
  }, [abortActiveRequest, setError, setLoading, setReport, setRequestStatus]);

  useEffect(() => {
    if (!skinReference) {
      setError('Missing skinreference in URL');
      setReport(null);
      setLoading(false);
      setRequestStatus(REQUEST_STATUS.FAILED);
      return undefined;
    }

    loadReport(skinReference, signedReportToken);
    return () => abortActiveRequest('unmount');
  }, [skinReference, signedReportToken, loadReport, abortActiveRequest, setError, setLoading, setReport, setRequestStatus]);

  // Poll while background Ollama report is still generating.
  useEffect(() => {
    if (!skinReference || !report) return undefined;

    const status = report?.ai_report?.status;
    const hasSummary = Boolean(String(report?.ai_report?.summary || '').trim());
    const shouldPoll = (status === 'pending' || status === 'processing') && !hasSummary;
    if (!shouldPoll) return undefined;

    const intervalId = setInterval(() => {
      loadReport(skinReference, signedReportToken, { silent: true });
    }, 4000);

    return () => clearInterval(intervalId);
  }, [skinReference, signedReportToken, report, loadReport]);

  const handleRetry = useCallback(() => {
    if (retryBusyRef.current) return;
    loadReport(skinReference, signedReportToken, { isManualRetry: true });
  }, [loadReport, skinReference, signedReportToken]);

  const statusText = STATUS_COPY[requestStatus] || STATUS_COPY[REQUEST_STATUS.PROCESSING];

  return (
    <div className="skin-routine-page-shell page-bg skin-only" id="skin-result-page-shell">
      {loading && (
        <main className="skin-routine-page-main">
          <div className="skin-routine-page-container">
            <div className="skin-routine-summary-loading" aria-live="polite">
              <div className="loading-spinner" />
              <p className="skin-routine-summary-loading-text">{statusText}</p>
            </div>
          </div>
        </main>
      )}

      {error && !loading && (
        <main className="skin-routine-page-main">
          <div className="skin-routine-page-container">
            <div className="skin-routine-error skin-result-error">
              <p>{error}</p>
              {(requestStatus === REQUEST_STATUS.RETRY_AVAILABLE
                || requestStatus === REQUEST_STATUS.TIMEOUT) && (
                <button type="button" className="skin-action-button" onClick={handleRetry}>
                  Try Again
                </button>
              )}
            </div>
          </div>
        </main>
      )}

      {!loading && !error && report && (
        <SkinResultLoadedView
          report={report}
          skinReference={skinReference}
          appSessionToken={appSessionToken}
        />
      )}
    </div>
  );
};

export default SkinResultPage;
